import { useMemo } from 'react';

import {
  getCurrentPremisesFollowUpTask,
  getLatestCoverageTaskByCanonicalType,
  getLatestPremisesSendLorTask,
  getLatestTaskByCanonicalType,
  isTaskNotApplicable,
} from '@/components/claims-filing/premises/premisesFlow';
import { useGetCoverageQuery } from '@/services/api/coverageService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import type { Task } from '@/services/types/client-intake-types';
import { CoverageCategory } from '@/services/types/coverage-types';
import {
  CanonicalTaskStatus,
  CanonicalTaskType,
} from '@/services/types/task-types';

export interface CoverageStepProgress {
  reviewCompletedAt?: string | null;
  reviewDone: boolean;
  callDone: boolean;
  /** "Write LOR" step is done when task is under review, sent, or completed */
  lorDone: boolean;
  /** "Review & Send LOR" step is done when task is sent or completed */
  reviewSendDone: boolean;
  ackDone: boolean;
  callCompletedAt?: string | null;
  lorCompletedAt?: string | null;
  ackCompletedAt?: string | null;
  isLoading: boolean;
  isError: boolean;
  // Premises-specific step progress
  confirmLocationDone: boolean;
  confirmLocationNA: boolean;
  researchPropertyOwnersDone: boolean;
  researchPropertyOwnersNA: boolean;
  /** "Write LOR" for premises: done when SEND_LOR is under review, sent, or completed */
  sendLorPremisesDone: boolean;
  sendLorPremisesNA: boolean;
  /** "Review & Send" for premises: done when SEND_LOR is sent or completed */
  reviewSendPremisesDone: boolean;
  // Premises-specific task IDs for linking to task details
  confirmLocationTaskId?: string | null;
  researchPropertyOwnersTaskId?: string | null;
  /** For premises, this is the regular SEND_LOR task linked to the property coverage */
  sendLorPremisesTaskId?: string | null;
  premisesFollowUpDone: boolean;
  premisesFollowUpNA: boolean;
  premisesFollowUpTaskId?: string | null;
  premisesFollowUpTaskType?:
    | CanonicalTaskType.OPEN_PREMISES_CLAIM
    | CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS
    | null;
}

const isCompleted = (task: Task | undefined): boolean => {
  if (!task) return false;
  // A task is considered complete if:
  // 1. canonical_status is COMPLETED, OR
  // 2. completed_at timestamp is set (fallback for data inconsistencies)
  return (
    task.status?.canonical_status === CanonicalTaskStatus.COMPLETED ||
    Boolean(task.completed_at)
  );
};

/**
 * For the "Write LOR" step, we consider it done when the LOR task is either
 * COMPLETED, UNDER_REVIEW, or SENT.
 * This prevents confusing UI where the upstream "Write LOR" step appears
 * incomplete while the downstream "Review & Send" step is in progress.
 */
const isLorStepDone = (task: Task | undefined): boolean => {
  if (!task) return false;
  const status = task.status?.canonical_status;
  const statusName = task.status?.name?.toLowerCase() || '';
  return (
    status === CanonicalTaskStatus.COMPLETED ||
    status === CanonicalTaskStatus.BLOCKED ||
    statusName.includes('under review') ||
    statusName.includes('sent')
  );
};

const isReviewSendStepDone = (task: Task | undefined): boolean => {
  if (!task) return false;
  const status = task.status?.canonical_status;
  const statusName = task.status?.name?.toLowerCase() || '';
  return (
    status === CanonicalTaskStatus.COMPLETED ||
    statusName.includes('sent')
  );
};
export const useCoverageStepProgress = (
  matterId: string,
  coverageId: string,
): CoverageStepProgress => {
  const {
    data: tasksResponse,
    isLoading,
    isError,
  } = useGetTasksQuery({
    matter: matterId,
    limit: 200,
  });
  const { data: coverage } = useGetCoverageQuery(
    { matterId, coverageId },
    { skip: !matterId || !coverageId },
  );

  const {
    reviewCompletedAt,
    reviewDone,
    callDone,
    lorDone,
    reviewSendDone,
    ackDone,
    callCompletedAt,
    lorCompletedAt,
    ackCompletedAt,
    // Premises-specific
    confirmLocationDone,
    confirmLocationNA,
    researchPropertyOwnersDone,
    researchPropertyOwnersNA,
    sendLorPremisesDone,
    sendLorPremisesNA,
    reviewSendPremisesDone,
    premisesFollowUpDone,
    premisesFollowUpNA,
    premisesFollowUpTaskId,
    premisesFollowUpTaskType,
    // Premises-specific task IDs
    confirmLocationTaskId,
    researchPropertyOwnersTaskId,
    sendLorPremisesTaskId,
  } = useMemo(() => {
    const tasks = tasksResponse?.results || [];
    const forCoverage = tasks.filter((t) => t.coverage_id === coverageId);

    const findByType = (type: CanonicalTaskType) =>
      getLatestCoverageTaskByCanonicalType(tasks, coverageId, type);

    // Find task by type in ALL matter tasks (for matter-level tasks like premises tasks)
    const findByTypeInMatter = (type: CanonicalTaskType) =>
      tasks.find((t) => t.type?.canonical_type === type);

    const reviewTask = findByType(CanonicalTaskType.REVIEW_FOUND_COVERAGE);
    const callTask = findByType(CanonicalTaskType.OPEN_CLAIM);
    const lorTask = findByType(CanonicalTaskType.SEND_LOR);
    const ackTask = findByType(CanonicalTaskType.LOR_ACKNOWLEDGEMENT);

    // Premises-specific tasks - these are matter-level tasks, not coverage-specific.
    const confirmLocationTask =
      findByType(CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES) ||
      findByTypeInMatter(CanonicalTaskType.REVIEW_INCIDENT_PROPERTIES);
    const researchPropertyTask = getLatestTaskByCanonicalType(
      forCoverage,
      CanonicalTaskType.RESEARCH_PROPERTY_OWNERS,
    );
    // For premises, the LOR task uses the regular SEND_LOR type linked to the coverage
    // and must remain coverage-specific.
    const sendLorPremisesTask =
      coverage?.category === CoverageCategory.PROPERTY
        ? getLatestPremisesSendLorTask(forCoverage)
        : undefined;
    const currentPremisesFollowUpTask =
      coverage?.category === CoverageCategory.PROPERTY
        ? getCurrentPremisesFollowUpTask(forCoverage)
        : undefined;

    // Consider discovery task completion (Find 1P/3P Coverage) as satisfying "Review Details"
    // A discovery task is complete if canonical_status is COMPLETED or completed_at is set
    let desiredDiscoveryType: CanonicalTaskType | undefined;
    if (coverage?.category === CoverageCategory.FIRST_PARTY) {
      desiredDiscoveryType = CanonicalTaskType.FIND_1P_COVERAGE;
    } else if (coverage?.category === CoverageCategory.THIRD_PARTY) {
      desiredDiscoveryType = CanonicalTaskType.FIND_3P_COVERAGE;
    }
    const discoveryTaskDone =
      desiredDiscoveryType !== undefined &&
      tasks.some(
        (t) =>
          t.type?.canonical_type === desiredDiscoveryType &&
          (t.status?.canonical_status === CanonicalTaskStatus.COMPLETED ||
            Boolean(t.completed_at)),
      );

    // Premises step completion checks
    const confirmLocationCompleted = isCompleted(confirmLocationTask);
    const confirmLocationNotApplicable =
      confirmLocationTask?.status?.canonical_status ===
      CanonicalTaskStatus.NOT_APPLICABLE;

    const researchPropertyCompleted = isCompleted(researchPropertyTask);
    const researchPropertyNotApplicable = isTaskNotApplicable(researchPropertyTask);

    // "Write LOR" for premises: done when under review OR completed
    const sendLorPremisesWriteDone = isLorStepDone(sendLorPremisesTask);
    // "Review & Send" for premises: done once correspondence has been queued/sent.
    const sendLorPremisesReviewSendDone =
      isReviewSendStepDone(sendLorPremisesTask);
    const sendLorPremisesNotApplicable = isTaskNotApplicable(sendLorPremisesTask);
    const premisesFollowUpCompleted = isCompleted(currentPremisesFollowUpTask);
    const premisesFollowUpNotApplicable = isTaskNotApplicable(
      currentPremisesFollowUpTask,
    );

    return {
      reviewCompletedAt: reviewTask?.completed_at ?? null,
      reviewDone: isCompleted(reviewTask) || Boolean(discoveryTaskDone),
      callDone: isCompleted(callTask),
      lorDone: isLorStepDone(lorTask),
      reviewSendDone:
        coverage?.category === CoverageCategory.PROPERTY
          ? isReviewSendStepDone(lorTask)
          : isCompleted(lorTask),
      ackDone: isCompleted(ackTask),
      callCompletedAt: callTask?.completed_at ?? null,
      lorCompletedAt: lorTask?.completed_at ?? null,
      ackCompletedAt: ackTask?.completed_at ?? null,
      // Premises-specific
      confirmLocationDone:
        confirmLocationCompleted || confirmLocationNotApplicable,
      confirmLocationNA: confirmLocationNotApplicable,
      researchPropertyOwnersDone:
        researchPropertyCompleted || researchPropertyNotApplicable,
      researchPropertyOwnersNA: researchPropertyNotApplicable,
      sendLorPremisesDone:
        sendLorPremisesWriteDone || sendLorPremisesNotApplicable,
      sendLorPremisesNA: sendLorPremisesNotApplicable,
      reviewSendPremisesDone:
        sendLorPremisesReviewSendDone || sendLorPremisesNotApplicable,
      premisesFollowUpDone:
        premisesFollowUpCompleted || premisesFollowUpNotApplicable,
      premisesFollowUpNA: premisesFollowUpNotApplicable,
      premisesFollowUpTaskId: currentPremisesFollowUpTask?.id ?? null,
      premisesFollowUpTaskType:
        currentPremisesFollowUpTask?.type?.canonical_type ===
          CanonicalTaskType.OPEN_PREMISES_CLAIM ||
        currentPremisesFollowUpTask?.type?.canonical_type ===
          CanonicalTaskType.RESEARCH_ALTERNATE_CONTACTS
          ? currentPremisesFollowUpTask.type.canonical_type
          : null,
      // Premises-specific task IDs
      confirmLocationTaskId: confirmLocationTask?.id ?? null,
      researchPropertyOwnersTaskId: researchPropertyTask?.id ?? null,
      sendLorPremisesTaskId: sendLorPremisesTask?.id ?? null,
    };
  }, [tasksResponse, coverageId, coverage?.category]);

  return {
    reviewCompletedAt,
    reviewDone,
    callDone,
    lorDone,
    reviewSendDone,
    ackDone,
    callCompletedAt,
    lorCompletedAt,
    ackCompletedAt,
    isLoading,
    isError,
    // Premises-specific
    confirmLocationDone,
    confirmLocationNA,
    researchPropertyOwnersDone,
    researchPropertyOwnersNA,
    sendLorPremisesDone,
    sendLorPremisesNA,
    reviewSendPremisesDone,
    premisesFollowUpDone,
    premisesFollowUpNA,
    premisesFollowUpTaskId,
    premisesFollowUpTaskType,
    // Premises-specific task IDs
    confirmLocationTaskId,
    researchPropertyOwnersTaskId,
    sendLorPremisesTaskId,
  };
};
